// ============================================================================
// Re-export Zod-derived entity types from schemas (ADR-0038)
// ============================================================================
// These types are the single source of truth for runtime-validated entities.
// They are derived from Zod schemas and represent TRANSFORMED data (e.g., Decimal -> number).
// ============================================================================
export type {
  // Primitive/Nested Types
  Attack,
  InventoryItem,
  Feature,
  PartySpell,
  PlotClue,
  PlotProgressPoint,
  EncounterCreature,

  // Backward compatibility aliases (schemas.ts uses different names)
  InventoryItem as PartyInventory,
  Feature as PartyFeature,

  // Core Entity Types
  PartyMember,
  NPC,
  POI,
  Monster,
  MapRegion,
  MapDrawing,
  Plot,
  Encounter,
  Relationship,

  // Combatant Types (discriminated union)
  BaseCombatant,
  PlayerCombatant,
  NonPlayerCombatant,
  Combatant,

  // World/Campaign Types
  WeatherCondition,
  WeatherForecast,
  Moon,
  MoonPhase,
  WeatherEvent,
  DailyAlmanac,
  World,
  Campaign,
  WorldCalendar,
  Deity,

  // Cache/Reference Types
  Image,
  Treasure,
  Item,
  Spell,
  PartyTreasure,
  Lore,
  Soundscape,

  // Auth Types
  User,

  // Category Types
  LoreCategory,
  ImageCategory,
  SoundscapeCategory,
  TerrainType,

  // Input Types (for API handling before Zod transformation)
  PartyMemberInput,
  NPCInput,
  POIInput,
  MapRegionInput,
  MapDrawingInput,
} from '../state/schemas';

// ============================================================================
// Prisma Types (for types not yet migrated to Zod schemas)
// ============================================================================
import type {
  CurrentEvent,
  GameSession as Session,
} from '@prisma/client';

export type { CurrentEvent, Session };

// ============================================================================
// UI-Specific Types (no Zod validation - used only in frontend)
// ============================================================================

// Import types needed for UI-specific type definitions
import type {
  Attack,
  PartyMember,
  NPC,
  Monster,
  POI,
  Deity,
  Lore,
  MapRegion,
  MapDrawing,
  Image,
  Encounter,
  Plot,
  LoreCategory,
  ImageCategory,
  TerrainType,
  Soundscape,
  SoundscapeCategory,
  Item,
  Spell,
  Relationship,
  WeatherEvent,
  MoonPhase,
  WeatherForecast,
  World,
  Campaign,
  WorldCalendar,
  DailyAlmanac,
  PartyTreasure,
  EncounterCreature,
} from '../state/schemas';

// ---- Entity Type Enum ----

/** String literal union for entity types used in relationships and combatants */
export type EntityType = 'npc' | 'player' | 'monster' | 'deity';

// ---- Canvas and Geometry Types ----

/** Point coordinates for canvas operations */
export interface Point {
  x: number;
  y: number;
}

/** Bounding rectangle for rendered images */
export interface RenderedImageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Form data for creating/editing map regions */
export interface RegionFormData {
  name: string;
  color: string;
  opacity: number;
  terrainTypeId: string | null;
}

/** Canvas interaction state for drawing tools */
export type InteractionState =
  | { type: 'line'; points: Point[] }
  | { type: 'grid-path'; start: Point; current: Point }
  | { type: 'cone'; start: Point; current: Point }
  | { type: 'shape'; shape: 'rect' | 'circle'; start: Point; current: Point }
  | { type: 'region'; id: string; selectedCells: Set<string> };

// ---- UI Wrapper Types ----

/** Event item for DataPanel display with synthetic name property */
export interface EventItem {
  id: number;
  text: string;
  name: string; // Synthetic property for DataPanel sorting
  sortOrder: number;
  [key: string]: unknown; // Index signature for compatibility
}

/** Extended relationship with resolved entity names for UI display */
export interface AugmentedRelationship {
  id: number;
  entity1Id: number;
  entity1Type: EntityType;
  entity2Id: number;
  entity2Type: EntityType;
  type: string;
  name: string; // Synthetic property
  npc1Name: string;
  npc1Type: EntityType | 'unknown';
  npc2Name: string;
  npc2Type: EntityType | 'unknown';
  notes?: string | null;
  [key: string]: unknown;
}

/** Attack with ID for form handling */
export interface AttackWithId extends Attack {
  id: number;
  [key: string]: unknown; // Add index signature for compatibility
}

/** Encounter result for UI display */
export interface EncounterResult {
  name: string;
  quantity: number;
  image?: string | null;
}

// ---- Combatant and Creature Types ----
// Note: BaseCombatant, PlayerCombatant, NonPlayerCombatant, and Combatant are
// now exported from schemas.ts as Zod-derived types (discriminated union)

/** Union of monster and NPC for shared creature handling */
export type Creature = Monster | NPC;

// ---- Grid Settings ----

/** Grid settings type imported from GridProvider */
export type GridSettings = import('../components/GridProvider/useGrid').GridSettings;

/** Default grid settings for new maps */
export const DEFAULT_GRID_SETTINGS: GridSettings = {
  enabled: false,
  type: 'square',
  size: 50,
  opacity: 0.5,
  offsetX: 0,
  offsetY: 0,
  showLabels: true,
  clipToImage: true,
  distancePerUnit: 5,
  distanceUnit: 'ft',
};

// ---- Calendar Types ----

/** Month definition for world calendars */
export interface Month {
  name: string;
  days: number;
}

// ---- State Types ----

import type { GeneralState } from '../state/generalActions';
import type { RosterState } from '../state/rosterActions';
import type { CombatState } from '../state/combatActions';
import type { PlotState } from '../state/plotActions';
import type { AuthActions, AuthState } from '../state/authActions';
import type { LoreActions, LoreState } from '../state/loreActions';
import type { GeneralActions } from '../state/generalActions';
import type { RosterActions } from '../state/rosterActions';
import type { CombatActions } from '../state/combatActions';
import type { PlotActions } from '../state/plotActions';
import type { MapActions, MapState } from '../state/mapActions';
import type { CacheActions, CacheState } from '../state/cacheActions';
import type { WorldActions } from '../state/worldActions';

/** World state slice containing worlds, campaigns, calendars, and related data */
export interface WorldState {
  worlds: World[];
  campaigns: Campaign[];
  worldCalendars: Record<number, WorldCalendar | null>;
  gameSessions: Record<number, Session[]>; // Keyed by campaignId
  almanac: DailyAlmanac[];
  weatherEvents: WeatherEvent[];
  partyTreasures: Record<number, PartyTreasure[]>; // Keyed by campaignId
  moonPhases: Record<number, MoonPhase[]>; // Keyed by moonId
  weatherForecasts: Record<number, WeatherForecast[]>; // Keyed by mapId
  moons: import('../state/schemas').Moon[];
}

/** Combined session state containing all domain slices */
export interface SessionState {
  general: GeneralState;
  roster: RosterState;
  combat: CombatState;
  plots: PlotState;
  map: MapState;
  lore: LoreState;
  cache: CacheState;
  world: WorldState;
}

// Action type composition - separates serializable state from non-serializable functions
export type ActionSlices = AuthActions &
  CombatActions &
  GeneralActions &
  MapActions &
  PlotActions &
  RosterActions &
  LoreActions &
  CacheActions &
  WorldActions;

export type WorldSlice = WorldState & WorldActions;
export type AllActions = ActionSlices;

/** Complete Zustand store state */
export interface StoreState {
  settings: {
    theme: string;
    units: string;
    fogOfWar: { enabled: boolean; data: string | null; eraserSize: number };
    grids: Record<string, GridSettings>;
    showMapRegions: boolean;
    showMapMarkers: boolean;
    activeDataViewTab?: string;
    activeWorldBuildingTab?: string;
    activeMapAssetsTab?: string;
    activeMainTab?: string;
    panelSettings?: {
      [key: string]: { filters?: Record<string, unknown>; sort?: string };
    };
  };
  auth: AuthState & AuthActions;
  session: SessionState;
  cache: CacheState & CacheActions;
  actions: AllActions;
  hydrateState: (newState: Partial<SessionState & { settings: StoreState['settings']; images: Image[] }>) => void;
}

// ---- Generic Constraints ----

/** Base type for entities with an ID */
export type Identifiable = { id: number };

/** Entity with ID and optional name, used for generic form handling */
export type ItemWithId = Identifiable & { name?: string; [key: string]: unknown };

// ---- Modal and Form Types ----

import type { Rule } from 'antd/es/form';
import type { FormInstance } from 'antd';

/** Context for editing an entity in a modal */
export interface EditContext<T extends ItemWithId> {
  type: string;
  item: T | null;
  onSave?: (item: T) => Promise<void> | void;
}

/** Internal state for modals that manage complex nested data */
export interface ModalInternalState {
  creatures?: EncounterCreature[];
  attacks?: AttackWithId[];
}

/** Props passed to custom form field components */
export interface CustomFormFieldComponentProps {
  item: ItemWithId;
  internalState: ModalInternalState;
  setInternalState: React.Dispatch<React.SetStateAction<ModalInternalState>>;
  form?: FormInstance;
}

/** Form field configuration for dynamic form generation */
export interface FormField {
  name: string | string[];
  label: string;
  type?: 'text' | 'number' | 'textarea' | 'select' | 'color' | 'range' | 'custom' | 'checkbox';
  rules?: Rule[];
  options?:
    | ((item: ItemWithId | null, state: StoreState) => { label: string; value: unknown }[])
    | { label: string; value: unknown }[];
  component?: React.FC<CustomFormFieldComponentProps>;
  [key: string]: unknown;
}

/** Tab configuration for tabbed forms */
export interface FormTab {
  id: string;
  label: string;
  fields: FormField[];
}

/** Button click handler for form actions */
export type ButtonHandler = (item: ItemWithId | null) => void;

/** Generic base for all entity-specific form configurations */
export interface EntityFormConfig<TItem extends ItemWithId, TCreate, TForm = Partial<TItem>> {
  title: string;
  width?: number;
  fields?: FormField[];
  tabs?: FormTab[];
  buttons?: {
    [key: string]: boolean | ((item: ItemWithId | null, state: StoreState) => boolean) | ButtonHandler;
  };
  addAction?: (item: TCreate) => Promise<void> | void;
  updateAction?: (item: TItem) => Promise<void> | void;
  onValuesChange?: (changedValues: Partial<TForm>, allValues: TForm, form: unknown) => void;
  createItemFromForm?: (data: TForm, state: StoreState) => TItem | Partial<TItem> | null;
  onOpen?: (
    item: TItem | null,
    state: StoreState
  ) => { formValues?: Partial<TForm>; internalState?: Partial<ModalInternalState> };
  onSave?: (
    item: TItem & Partial<TForm>,
    isNew: boolean,
    internalState: ModalInternalState,
    state: StoreState
  ) => Promise<Partial<TCreate>> | void | Partial<TCreate>;
}

// ---- Entity-Specific Form Configurations ----

export type PlayerFormConfig = EntityFormConfig<
  PartyMember,
  Omit<PartyMember, 'id'>,
  Partial<PartyMember> & { stats?: Record<string, number> }
>;
export type MonsterRosterFormConfig = EntityFormConfig<Monster, Omit<Monster, 'id'>, Partial<Monster>>;
export type NpcFormConfig = EntityFormConfig<NPC, Omit<NPC, 'id'>, Partial<NPC>>;
export type PoiFormConfig = EntityFormConfig<POI, Omit<POI, 'id'>, Partial<POI>>;
export type DeityFormConfig = EntityFormConfig<Deity, Omit<Deity, 'id'>, Partial<Deity>>;
export type LoreFormConfig = EntityFormConfig<Lore, Omit<Lore, 'id'>, Partial<Lore>>;
export type MapRegionFormConfig = EntityFormConfig<
  MapRegion,
  Omit<MapRegion, 'id'>,
  Omit<Partial<MapRegion>, 'color' | 'opacity'> & { color?: { color: string; opacity: number } }
>;
export type MapDrawingFormConfig = EntityFormConfig<MapDrawing, Omit<MapDrawing, 'id'>, Partial<MapDrawing>>;
export type ImageLibraryFormConfig = EntityFormConfig<Image, Omit<Image, 'id'>, Partial<Image>>;
export type EncounterFormConfig = EntityFormConfig<Encounter, Omit<Encounter, 'id'>, Partial<Encounter>>;
export type PlotFormConfig = EntityFormConfig<Plot, string, Partial<Plot>>;
export type LoreCategoryFormConfig = EntityFormConfig<LoreCategory, Omit<LoreCategory, 'id'>, Partial<LoreCategory>>;
export type ImageCategoryFormConfig = EntityFormConfig<ImageCategory, Omit<ImageCategory, 'id'>, Partial<ImageCategory>>;
export type TerrainTypeFormConfig = EntityFormConfig<
  TerrainType,
  Omit<TerrainType, 'id' | 'createdAt' | 'updatedAt'>,
  Partial<TerrainType>
>;
export type SoundscapeFormConfig = EntityFormConfig<
  Soundscape,
  Omit<Soundscape, 'id' | 'createdAt'>,
  Partial<Soundscape>
>;
export type ItemFormConfig = EntityFormConfig<Item, Omit<Item, 'id'>, Partial<Item>>;
export type SpellFormConfig = EntityFormConfig<Spell, Omit<Spell, 'id'>, Partial<Spell>>;
export type SoundscapeCategoryFormConfig = EntityFormConfig<
  SoundscapeCategory,
  Omit<SoundscapeCategory, 'id'>,
  Partial<SoundscapeCategory>
>;

// Union of all possible entity types for generic handling
export type AnyItem =
  | PartyMember
  | Monster
  | NPC
  | POI
  | Deity
  | Lore
  | MapRegion
  | MapDrawing
  | Image
  | Encounter
  | Plot
  | LoreCategory
  | ImageCategory
  | TerrainType
  | Soundscape
  | Item
  | Spell
  | Relationship
  | AugmentedRelationship;

// Union of all possible form configurations
export type AnyFormConfig =
  | PlayerFormConfig
  | MonsterRosterFormConfig
  | NpcFormConfig
  | PoiFormConfig
  | DeityFormConfig
  | LoreFormConfig
  | MapRegionFormConfig
  | MapDrawingFormConfig
  | ImageLibraryFormConfig
  | EncounterFormConfig
  | PlotFormConfig
  | LoreCategoryFormConfig
  | ImageCategoryFormConfig
  | TerrainTypeFormConfig
  | SoundscapeFormConfig
  | SoundscapeCategoryFormConfig
  | ItemFormConfig
  | SpellFormConfig;

/** Complete form configuration map for all entity types */
export type FormConfig = {
  player: PlayerFormConfig;
  monsterRoster: MonsterRosterFormConfig;
  npc: NpcFormConfig;
  poi: PoiFormConfig;
  deity: DeityFormConfig;
  lore: LoreFormConfig;
  mapRegion: MapRegionFormConfig;
  mapDrawing: MapDrawingFormConfig;
  imageLibrary: ImageLibraryFormConfig;
  encounter: EncounterFormConfig;
  plot: PlotFormConfig;
  loreCategory: LoreCategoryFormConfig;
  imageCategory: ImageCategoryFormConfig;
  terrainType: TerrainTypeFormConfig;
  soundscape: SoundscapeFormConfig;
  item: ItemFormConfig;
  spell: SpellFormConfig;
  soundscapeCategory: SoundscapeCategoryFormConfig;
  // Fallback for any other keys
  [key: string]: AnyFormConfig;
};

// ---- JSON Types ----

/** Generic JSON value type, compatible with Prisma's JsonValue */
export type JsonValue = string | number | boolean | { [x: string]: JsonValue } | Array<JsonValue> | null;
export type JsonObject = { [key: string]: JsonValue };
export type JsonArray = JsonValue[];

// ---- Audit Log ----

/** Audit log entry for tracking user actions */
export interface AuditLog {
  id: number;
  userId: number | null;
  user: {
    username: string;
  } | null;
  actionType: string;
  targetType: string;
  targetId: string;
  details: JsonValue | null;
  createdAt: string;
  ipAddress: string | null;
}
